import React from "react";
import { cn } from "../../utils/cn";

interface ResponsiveGridProps extends React.HTMLAttributes<HTMLDivElement> {
  cols?: {
    default?: 1 | 2 | 3 | 4 | 6 | 12;
    sm?: 1 | 2 | 3 | 4 | 6 | 12;
    md?: 1 | 2 | 3 | 4 | 6 | 12;
    lg?: 1 | 2 | 3 | 4 | 6 | 12 | number;
    xl?: 1 | 2 | 3 | 4 | 6 | 12;
  };
  gap?: "sm" | "md" | "lg" | "xl";
}

const ResponsiveGrid: React.FC<ResponsiveGridProps> = ({
  className,
  cols = { default: 1, sm: 2, md: 3, lg: 4 },
  gap = "md",
  children,
  ...props
}) => {
  const gapClasses = {
    sm: "gap-2.5 sm:gap-3 md:gap-3.5 lg:gap-4",
    md: "gap-3 sm:gap-3.5 md:gap-4 lg:gap-5 xl:gap-6",
    lg: "gap-3.5 sm:gap-4 md:gap-5 lg:gap-6 xl:gap-8",
    xl: "gap-4 sm:gap-5 md:gap-6 lg:gap-8 xl:gap-10",
  };

  // Build responsive grid classes using predefined Tailwind classes
  const buildGridClasses = () => {
    // items-start evita que o grid estique todos os cards da mesma linha até a altura do mais
    // alto (comportamento padrão do CSS Grid é align-items: stretch) - cada card mantém sua
    // altura natural, sem espaço vazio sobrando depois do último conteúdo real.
    const classes: string[] = ["grid", "items-start"];

    // Default (mobile first)
    const defaultCols = cols.default || 1;
    const defaultMap: Record<number, string> = {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
      6: "grid-cols-6",
      12: "grid-cols-12",
    };
    classes.push(defaultMap[defaultCols] || "grid-cols-1");

    // Responsive breakpoints
    const breakpointMap: Record<number, string> = {
      1: "grid-cols-1",
      2: "grid-cols-2",
      3: "grid-cols-3",
      4: "grid-cols-4",
      6: "grid-cols-6",
      12: "grid-cols-12",
    };

    if (cols.sm) {
      classes.push(`sm:${breakpointMap[cols.sm]}`);
    }

    if (cols.md) {
      classes.push(`md:${breakpointMap[cols.md]}`);
    }

    if (cols.lg) {
      const lgValue = typeof cols.lg === "number" ? cols.lg : cols.lg;
      if (lgValue <= 12 && lgValue >= 1) {
        const mappedValue =
          breakpointMap[lgValue as keyof typeof breakpointMap] ||
          "grid-cols-12";
        classes.push(`lg:${mappedValue}`);
      }
    }

    if (cols.xl) {
      classes.push(`xl:${breakpointMap[cols.xl]}`);
    }

    return classes.join(" ");
  };

  return (
    <div
      className={cn(buildGridClasses(), gapClasses[gap], className)}
      {...props}
    >
      {children}
    </div>
  );
};

export default ResponsiveGrid;
